import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { useRecoilState, useSetRecoilState } from "recoil";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { useUserPermission } from "@/hooks/use-permission";
import { Permissions } from "@/components/CheckPermission/enum";
import { FONT_FAMILY } from "@/utils/font-family";
import { pendingInternalMessageToastAtom } from "@/store/frontdesk/atoms/notifications.atoms";
import {
  internalChatPopupConversationIdAtom,
  internalChatPopupMinimizedAtom,
  internalChatPopupOpenAtom,
} from "@/store/frontdesk/atoms/internal-chat-popup.atoms";
import { directMessagesService } from "@/services/frontdesk/api/direct-messages.service";
import { formatInternalChatPreview } from "@/widgets/quote-new/buttons-container/build-quote-approval-message";

const TOAST_MS = 8000;
const MAX_PREVIEW = 140;

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function previewFromQuoteMetadata(
  meta: Record<string, unknown> | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | null {
  if (!meta || typeof meta !== "object") return null;

  if (meta.type === "quote_approval_result") {
    const outcome = meta.outcome === "rejected" ? "rejected" : "approved";
    const number = String(meta.quoteNumber || "N/A");
    return outcome === "approved"
      ? t("sales.quote.quoteApprovalManagerApproved", {
          number,
          defaultValue: `Quote ${number} was approved by the manager.`,
        })
      : t("sales.quote.quoteApprovalManagerRejected", {
          number,
          defaultValue: `Quote ${number} was rejected by the manager.`,
        });
  }

  if (meta.type !== "quote_approval") return null;
  const qa = (meta.quoteApproval || {}) as Record<string, unknown>;
  const title = t("sales.quote.quoteApprovalRequestTitle", {
    defaultValue: "Quote Approval Request",
  });
  const parts = [`📄 ${title}`];
  const quoteNumber = String(qa.quoteNumber || "").trim();
  const clientName = String(qa.clientName || "").trim();
  const totalPrice = String(qa.totalPrice || "").trim();
  if (quoteNumber) {
    parts.push(
      `${t("sales.quote.quoteApprovalQuoteNumber", {
        defaultValue: "Quote Number",
      })}: ${quoteNumber}`,
    );
  }
  if (clientName) {
    parts.push(
      `${t("sales.quote.quoteApprovalClientName", {
        defaultValue: "Client Name",
      })}: ${clientName}`,
    );
  }
  if (totalPrice) {
    parts.push(
      `${t("sales.quote.quoteApprovalTotalPrice", {
        defaultValue: "Total Price",
      })}: ${totalPrice}`,
    );
  }
  return parts.join(" · ");
}

function previewText(
  content: string | undefined,
  fallback: string,
  t: (key: string, options?: Record<string, unknown>) => string,
  messageMetadata?: Record<string, unknown>,
): string {
  const fromMeta = previewFromQuoteMetadata(messageMetadata, t);
  const localized = (
    fromMeta || formatInternalChatPreview(content, t)
  )
    .replace(/\s+/g, " ")
    .trim();
  const text = localized || (content || "").replace(/\s+/g, " ").trim();
  if (!text) return fallback;
  if (text.length <= MAX_PREVIEW) return text;
  return `${text.slice(0, MAX_PREVIEW - 1)}…`;
}

/**
 * WhatsApp-style toast for incoming internal DMs, with quick reply.
 * Shown when notification polling finds a new DM (not when opening the envelope).
 * EN (LTR): bottom-right. HE/AR (RTL): bottom-left.
 */
const InternalMessageToastHost = () => {
  const { t } = useTranslation();
  const dir: "rtl" | "ltr" =
    t("direction") === "rtl" ||
    (typeof document !== "undefined" &&
      document.documentElement.getAttribute("dir") === "rtl")
      ? "rtl"
      : "ltr";
  const router = useRouter();
  const { primaryColor, neutralColor } = useGomakeTheme();
  const { CheckPermission } = useUserPermission();
  const canShowFrontDesk = CheckPermission(Permissions.SHOW_FRONT_DESK);

  const [pending, setPending] = useRecoilState(pendingInternalMessageToastAtom);
  const [chatPopupOpen] = useRecoilState(internalChatPopupOpenAtom);
  const [chatPopupMinimized] = useRecoilState(internalChatPopupMinimizedAtom);
  const [chatPopupConversationId] = useRecoilState(
    internalChatPopupConversationIdAtom,
  );
  const setChatPopupOpen = useSetRecoilState(internalChatPopupOpenAtom);
  const setChatPopupMinimized = useSetRecoilState(
    internalChatPopupMinimizedAtom,
  );
  const setChatPopupConversationId = useSetRecoilState(
    internalChatPopupConversationIdAtom,
  );
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<typeof pending>(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [replyFocused, setReplyFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const resetComposer = useCallback(() => {
    setReply("");
    setSending(false);
    setSendError(null);
    setReplyFocused(false);
  }, []);

  useEffect(() => {
    if (!canShowFrontDesk || !pending) return;

    const activeConversation = String(router.query?.conversation || "");
    if (
      activeConversation &&
      activeConversation === pending.conversationId
    ) {
      setPending(null);
      return;
    }

    // Don't toast if the floating internal chat already shows this thread
    if (
      chatPopupOpen &&
      !chatPopupMinimized &&
      chatPopupConversationId === pending.conversationId
    ) {
      setPending(null);
      return;
    }

    setActive(pending);
    setOpen(true);
    resetComposer();
    setPending(null);
  }, [
    canShowFrontDesk,
    pending,
    router.query?.conversation,
    chatPopupOpen,
    chatPopupMinimized,
    chatPopupConversationId,
    setPending,
    resetComposer,
  ]);

  const handleClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    // Keep open while typing / sending a reply
    if (reason === "timeout" && (replyFocused || reply.trim() || sending)) {
      return;
    }
    setOpen(false);
    resetComposer();
  };

  const handleOpenConversation = () => {
    const conversationId = active?.conversationId;
    setOpen(false);
    resetComposer();
    if (!conversationId) return;
    setChatPopupConversationId(conversationId);
    setChatPopupMinimized(false);
    setChatPopupOpen(true);
  };

  const handleSendReply = async () => {
    const conversationId = active?.conversationId;
    const content = reply.trim();
    if (!conversationId || !content || sending) return;

    setSending(true);
    setSendError(null);
    try {
      await directMessagesService.sendMessage(conversationId, { content });
      setOpen(false);
      resetComposer();
    } catch (err) {
      console.error("[InternalMessageToast] Quick reply failed:", err);
      setSendError(
        t("frontdesk.notifications.internalMessageToast.replyFailed")
      );
      setSending(false);
    }
  };

  if (!canShowFrontDesk) return null;

  const senderName =
    active?.senderName ||
    t("frontdesk.notifications.internalMessageToast.fallbackSender");
  const body = previewText(
    active?.content,
    t("frontdesk.notifications.internalMessageToast.newMessage"),
    t,
    active?.messageMetadata,
  );
  const canSend = Boolean(reply.trim()) && !sending;

  return (
    <Snackbar
      open={open}
      autoHideDuration={
        replyFocused || reply.trim() || sending ? null : TOAST_MS
      }
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: dir === "rtl" ? "left" : "right",
      }}
      sx={{
        ...(dir === "rtl"
          ? { left: "24px !important", right: "auto !important" }
          : { right: "24px !important", left: "auto !important" }),
        bottom: "24px !important",
        zIndex: (theme) => theme.zIndex.snackbar + 10,
      }}
    >
      <Paper
        elevation={8}
        aria-label={t("frontdesk.notifications.internalMessageToast.ariaLabel", {
          name: senderName,
        })}
        sx={{
          width: { xs: "calc(100vw - 32px)", sm: 360 },
          maxWidth: 380,
          borderRadius: "12px",
          overflow: "hidden",
          bgcolor: "#F3F4F6",
          border: `1px solid ${neutralColor(200)}`,
          boxShadow:
            "0 8px 24px rgba(15, 23, 42, 0.18), 0 2px 6px rgba(15, 23, 42, 0.08)",
          direction: dir,
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: "12px", pt: "10px", pb: "4px" }}
        >
          <Stack direction="row" alignItems="center" gap="6px" minWidth={0}>
            <ChatIcon sx={{ fontSize: 16, color: primaryColor(500) }} />
            <Typography
              noWrap
              sx={{
                ...FONT_FAMILY.Lexend(500, 12),
                color: neutralColor(700),
              }}
            >
              {t("frontdesk.notifications.internalMessageToast.channel")}
            </Typography>
          </Stack>
          <IconButton
            size="small"
            aria-label={t("frontdesk.notifications.internalMessageToast.close")}
            onClick={() => {
              setOpen(false);
              resetComposer();
            }}
            sx={{ p: "2px", color: neutralColor(500) }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>

        {/* Message preview — click opens conversation */}
        <Stack
          direction="row"
          alignItems="center"
          gap="12px"
          onClick={handleOpenConversation}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleOpenConversation();
            }
          }}
          sx={{
            px: "12px",
            py: "8px",
            cursor: "pointer",
            "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
          }}
        >
          <Avatar
            src={active?.senderAvatar || undefined}
            alt={senderName}
            sx={{
              width: 40,
              height: 40,
              bgcolor: primaryColor(100),
              color: primaryColor(700),
              ...FONT_FAMILY.Lexend(600, 13),
            }}
          >
            {active?.senderAvatar ? null : initialsFromName(senderName)}
          </Avatar>
          <Stack spacing={0.15} sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                ...FONT_FAMILY.Lexend(600, 14),
                color: neutralColor(900),
              }}
            >
              {senderName}
            </Typography>
            <Typography
              sx={{
                ...FONT_FAMILY.Lexend(400, 13),
                color: neutralColor(600),
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {body}
            </Typography>
          </Stack>
        </Stack>

        {/* Quick reply */}
        <Stack
          direction="row"
          alignItems="center"
          gap="8px"
          sx={{
            px: "12px",
            pb: "12px",
            pt: "4px",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              bgcolor: "#FFFFFF",
              borderRadius: "8px",
              border: `1px solid ${
                replyFocused ? primaryColor(400) : neutralColor(200)
              }`,
              px: "10px",
              minHeight: 36,
            }}
          >
            <InputBase
              inputRef={inputRef}
              fullWidth
              value={reply}
              disabled={sending}
              placeholder={t(
                "frontdesk.notifications.internalMessageToast.replyPlaceholder"
              )}
              onChange={(e) => {
                setReply(e.target.value);
                if (sendError) setSendError(null);
              }}
              onFocus={() => setReplyFocused(true)}
              onBlur={() => setReplyFocused(false)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void handleSendReply();
                }
                e.stopPropagation();
              }}
              sx={{
                ...FONT_FAMILY.Lexend(400, 13),
                color: neutralColor(900),
              }}
              inputProps={{
                "aria-label": t(
                  "frontdesk.notifications.internalMessageToast.replyPlaceholder"
                ),
              }}
            />
          </Box>
          <IconButton
            size="small"
            disabled={!canSend}
            onClick={() => void handleSendReply()}
            aria-label={t("frontdesk.notifications.internalMessageToast.send")}
            sx={{
              bgcolor: canSend ? primaryColor(500) : neutralColor(200),
              color: canSend ? "#FFFFFF" : neutralColor(400),
              width: 36,
              height: 36,
              borderRadius: "8px",
              "&:hover": {
                bgcolor: canSend ? primaryColor(600) : neutralColor(200),
              },
              "&.Mui-disabled": {
                bgcolor: neutralColor(200),
                color: neutralColor(400),
              },
            }}
          >
            {sending ? (
              <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
            ) : (
              <SendIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Stack>

        {sendError ? (
          <Typography
            sx={{
              ...FONT_FAMILY.Lexend(400, 11),
              color: "#DC2626",
              px: "12px",
              pb: "10px",
              mt: "-6px",
            }}
          >
            {sendError}
          </Typography>
        ) : null}
      </Paper>
    </Snackbar>
  );
};

export { InternalMessageToastHost };
